<template>
  <div
    :id="'imageGallery' + id"
    class="position-relative"
    @click="registerInteraction()"
    @mouseenter="mouseOver = true; fadeInHint()"
    @mouseleave="mouseOver = false; fadeOutHint()"
  >
    <div
      v-if="getImageCount() > 1"
      :class="thumbnailPositionClasses"
      class="slider"
    >
      <div
        class="slides"
        @scroll="registerInteraction()"
      >
        <a
          v-for="(image, index) in singleImages"
          :id="'slide-' + id + '-' + index"
          :key="'slide-' + index"
          :data-pswp-height="image.height"
          :data-pswp-width="image.width"
          :href="image.url + '.avif'"
          :title="getImageName(image)"
          class="slide pswp-slide"
          itemscope
          itemtype="https://schema.org/ImageObject"
          target="_blank"
        >
          <meta
            :content="getImageName(image)"
            itemprop="name"
          >
          <meta
            :content="$translate('Ceres::Template.headerCompanyName')"
            itemprop="copyrightNotice"
          >
          <img
            v-if="index === 0"
            :alt="getAltText(image)"
            :height="image.height"
            :sizes="imageSizes"
            :src="image.urlMiddle + '.avif'"
            :srcset="getImageSrcSet(image)"
            :width="image.width"
            fetchpriority="high"
            itemprop="contentUrl"
          >
          <img
            v-else
            :alt="getAltText(image)"
            :height="image.height"
            :sizes="imageSizes"
            :src="image.urlMiddle + '.avif'"
            :srcset="getImageSrcSet(image)"
            :width="image.width"
            decoding="async"
            itemprop="contentUrl"
            loading="lazy"
          >
        </a>
      </div>
      <div
        :class="thumbnailDisplayClasses"
        class="slide-links"
      >
        <a
          v-for="(image, index) in singleImages"
          :key="'slide-link' + index"
          :href="'#slide-' + id + '-' + index"
          @click.prevent="scrollToImage(index)"
        >
          <img
            :alt="getAltText(image)"
            :height="300"
            :src="image.urlPreview + '.avif'"
            :width="300"
            decoding="async"
            loading="lazy"
          >
        </a>
      </div>
    </div>

    <a
      v-else
      key="0"
      :data-pswp-height="singleImages[0].height"
      :data-pswp-width="singleImages[0].width"
      :href="singleImages[0].url + '.avif'"
      :title="getImageName(singleImages[0])"
      class="pswp-slide d-block prop-1-1"
      itemscope
      itemtype="https://schema.org/ImageObject"
      target="_blank"
    >
      <meta
        :content="getImageName(singleImages[0])"
        itemprop="name"
      >
      <meta
        :content="$translate('Ceres::Template.headerCompanyName')"
        itemprop="copyrightNotice"
      >
      <img
        :alt="getAltText(singleImages[0])"
        :height="singleImages[0].height"
        :sizes="imageSizes"
        :src="singleImages[0].urlMiddle + '.avif'"
        :srcset="getImageSrcSet(singleImages[0])"
        :width="singleImages[0].width"
        class="w-100 h-100 position-absolute"
        fetchpriority="high"
        itemprop="contentUrl"
        style="object-fit: contain"
      >
    </a>

    <transition name="fade">
      <div
        v-if="showHint"
        :class="hintCssClasses"
        :style="hintCssStyles"
        class="slide-hints position-absolute d-flex no-pointer-events align-items-center"
      >
        <img
          v-if="zoomImagePath"
          :alt="zoomTextHint"
          :src="zoomImagePath"
          class="mx-2"
          height="24"
          width="24"
        >
        <span
          v-else
          class="fa-2x fa fa-plus-square-o mx-2"
        />
        <span :class="{'sr-only': !showTextHint}">{{ zoomTextHint }}</span>
        <img
          v-if="getImageCount() > 1 && swipeImagePath"
          :alt="swipeTextHint"
          :src="swipeImagePath"
          class="mx-2"
          height="24"
          width="24"
        >
        <span
          v-else-if="getImageCount() > 1"
          class="fa-2x fa fa-arrows-h mx-2"
        />
        <span
          v-if="getImageCount() > 1"
          :class="{'sr-only': !showTextHint}"
        >{{ swipeTextHint }}</span>
      </div>
    </transition>
  </div>
</template>

<script>

export default {
  name: 'AdvancedItemImageCarousel',

  inject: {
    itemId: {
      default: null
    }
  },

  props: {
    maxQuantity:
        {
          type: Number,
          default: 10
        },
    showIcons:
        {
          type: Boolean,
          default: true
        },
    zoomImagePath:
        {
          type: String,
          default: ''
        },
    swipeImagePath:
        {
          type: String,
          default: ''
        },
    showTextHint:
        {
          type: Boolean,
          default: true
        },
    zoomTextHint:
        {
          type: String,
          default: ''
        },
    swipeTextHint:
        {
          type: String,
          default: ''
        },
    hintDisplayOption:
        {
          type: String,
          default: 'until_first_interaction'
        },
    imageWidths:
        {
          type: Array,
          default: () => []
        },
    imageSizes:
        {
          type: String,
          default: ''
        },
    thumbnailPositionClasses:
        {
          type: String,
          default: 'flex-column'
        },
    thumbnailDisplayClasses:
        {
          type: String,
          default: 'd-none'
        },
    hintCssClasses:
        {
          type: String,
          default: ''
        },
    hintCssStyles:
        {
          type: String,
          default: ''
        }
  },

  data () {
    return {
      initializationId: 0,
      imageObserver: null,
      id: null,
      showHint: true,
      interacted: false,
      mouseOver: false,
      lightbox: null,
      hideHintTimer: null,
      activeImageIndex: 0
    }
  },

  computed: {
    currentVariation () {
      return this.$store.getters[`${this.itemId}/currentItemVariation`]
    },

    singleImages () {
      const imagesAccessor = (this.currentVariation.images.variation && this.currentVariation.images.variation.length) ? 'variation' : 'all'
      return this.currentVariation.images[imagesAccessor].slice(0, this.maxQuantity)
    },

    imageSignature () {
      return this.singleImages
        .map(image => image.url)
        .join('|')
    }
  },

  watch: {
    imageSignature () {
      this.$nextTick(() => {
        this.reInitialize()
      })
    }
  },

  mounted () {
    this.id = this._uid
    this.showHint = ['always', 'until_first_interaction'].includes(this.hintDisplayOption)

    this.$nextTick(() => {
      this.initialize()
    })
  },

  unmounted () {
    this.destroy()
  },

  beforeDestroy () {
    this.destroy()
  },

  methods:
      {
        getImageCount () {
          return this.singleImages.length
        },

        getAltText (image) {
          let altText = image && image.alternate ? image.alternate : this.$options.filters.itemName(this.currentVariation)
          if (this.currentVariation.attributes.length > 0) {
            altText = altText + ' ' + this.currentVariation.attributes[0].value.names.name
          }
          return altText
        },

        getImageName (image) {
          let imageName = image && image.name ? image.name : this.$options.filters.itemName(this.currentVariation)
          if (this.currentVariation.attributes.length > 0) {
            imageName = imageName + ' ' + this.currentVariation.attributes[0].value.names.name
          }
          return imageName
        },

        getImageSrcSet (image) {
          return `${image.urlPreview}.avif ${this.imageWidths[0]}w, ${image.urlSecondPreview}.avif ${this.imageWidths[1]}w, ${image.urlMiddle}.avif ${this.imageWidths[2]}w`
        },

        async initialize () {
          const initializationId = ++this.initializationId

          try {
            const { default: PhotoSwipeLightbox } = await import('photoswipe/lightbox')

            if (initializationId !== this.initializationId) {
              return
            }

            const lightbox = new PhotoSwipeLightbox({
              gallery: '#imageGallery' + this.id,
              children: '.pswp-slide',
              pswpModule: () => import('photoswipe')
            })

            // keep image index in sync between both galleries
            lightbox.on('change', () => {
              if (this.lightbox?.pswp) {
                this.activeImageIndex = lightbox.pswp.currIndex
              }
            })

            lightbox.on('closingAnimationEnd', () => {
              if (
                initializationId !== this.initializationId ||
                this.lightbox !== lightbox
              ) {
                return
              }

              this.$nextTick(() => {
                this.scrollToImage(this.activeImageIndex, 'auto')
              })
            })

            if (initializationId !== this.initializationId) {
              lightbox.destroy()
              return
            }

            this.lightbox = lightbox
            lightbox.init()
          } catch (error) {
            if (initializationId === this.initializationId) {
              console.error(
                'Fehler beim Laden von PhotoSwipeLightbox:',
                error
              )
            }
          }

          this.fadeOutHint()
        },

        destroy () {
          this.initializationId++
          this.clearHideHintTimer()

          if (this.lightbox) {
            this.lightbox.destroy()
            this.lightbox = null
          }
        },

        reInitialize () {
          this.destroy()

          this.$nextTick(() => {
            this.initialize()
          })
        },

        registerInteraction () {
          if (!this.interacted && this.hintDisplayOption === 'until_first_interaction') {
            this.interacted = true
            this.startHideHintTimer()
          }
        },

        fadeInHint () {
          if (this.hintDisplayOption === 'hover' ||
              (this.hintDisplayOption === 'until_first_interaction' && !this.interacted)) {
            this.showHint = true
            this.clearHideHintTimer()
          }
        },

        fadeOutHint () {
          if (['until_first_interaction', 'hover'].includes(this.hintDisplayOption)) {
            this.startHideHintTimer()
          }
        },

        startHideHintTimer () {
          this.clearHideHintTimer()
          this.hideHintTimer = setTimeout(() => {
            this.showHint = false
          }, 3000)
        },

        clearHideHintTimer () {
          if (this.hideHintTimer) {
            clearTimeout(this.hideHintTimer)
            this.hideHintTimer = null
          }
        },

        scrollToImage (index, behavior = 'smooth') {
          const targetSlide = document.getElementById(`slide-${this.id}-${index}`)

          if (targetSlide) {
            const slidesContainer = targetSlide.parentElement

            slidesContainer.scrollTo({
              left: targetSlide.offsetLeft,
              behavior
            })
          }
          this.registerInteraction()
        }
      }

}
</script>

<style scoped>
.fade-enter-active, .fade-leave-active {
  transition: opacity .5s;
}

.fade-enter, .fade-leave-to {
  opacity: 0;
}
</style>
